import reverse from 'lodash/reverse';
import sortBy from 'lodash/sortBy';

import { storableError } from '../../util/errors';
import * as log from '../../util/log';
import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import {
  guardedTransaction,
  processTransactionSmsNotifications,
  sendMarketplaceMessage,
} from '../../util/api';
import { types as sdkTypes } from '../../util/sdkLoader';
import { OFFER_TRANSITIONS, filterOfferConversations } from '../../util/offerHelpers';
import { parse } from '../../util/urlHelpers';
import { getTransitionCandidateNames } from '../../transactions/transitionAliases';
import { PURCHASE_PROCESS_NAME } from '../../transactions/transaction';

const { UUID } = sdkTypes;

export const FETCH_OFFERS_REQUEST = 'app/OffersPage/FETCH_OFFERS_REQUEST';
export const FETCH_OFFERS_SUCCESS = 'app/OffersPage/FETCH_OFFERS_SUCCESS';
export const FETCH_OFFERS_ERROR = 'app/OffersPage/FETCH_OFFERS_ERROR';

export const UPDATE_OFFER_PROGRESS = 'app/OffersPage/UPDATE_OFFER_PROGRESS';
export const UPDATE_OFFER_SUCCESS = 'app/OffersPage/UPDATE_OFFER_SUCCESS';
export const UPDATE_OFFER_ERROR = 'app/OffersPage/UPDATE_OFFER_ERROR';

const sortedTransactions = txs =>
  reverse(
    sortBy(txs, tx => {
      return tx.attributes ? tx.attributes.lastTransitionedAt : null;
    })
  );

const triggerTransactionSmsNotifications = ({ transactionId, source, transition }) => {
  if (!transactionId) {
    return;
  }

  processTransactionSmsNotifications({ transactionId, source, transition }).catch(e => {
    log.warn(e, 'transaction-sms-direct-trigger-failed', {
      source,
      transactionId: transactionId?.uuid || transactionId,
      transition,
    });
  });
};

const uniqueValues = values => Array.from(new Set(values.filter(Boolean)));
const withReleaseAliases = processNames =>
  processNames.flatMap(processName =>
    processName.includes('/') ? [processName] : [processName, `${processName}/release-1`]
  );

const OFFER_PAGE_SIZE = 25;
const OFFER_PROCESS_NAMES = uniqueValues(
  withReleaseAliases([
    PURCHASE_PROCESS_NAME,
    'default-buying-products',
    'flex-product-default-process',
    'offer-process',
    'wallet-process',
  ])
).join(',');
const OFFER_LAST_TRANSITIONS = uniqueValues(
  OFFER_TRANSITIONS.flatMap(transitionName => getTransitionCandidateNames(transitionName))
);
const USER_FIELDS = [
  'profile.displayName',
  'profile.abbreviatedName',
  'profile.publicData',
  'deleted',
  'banned',
  'state',
  'disabled',
];
const offerResponseQueryParams = {
  include: [
    'listing',
    'listing.images',
    'provider',
    'provider.profileImage',
    'customer',
    'customer.profileImage',
    'booking',
  ],
  'fields.transaction': [
    'processName',
    'lastTransition',
    'lastTransitionedAt',
    'createdAt',
    'state',
    'transitions',
    'payinTotal',
    'payoutTotal',
    'lineItems',
    'protectedData',
    'metadata',
  ],
  'fields.listing': ['title', 'publicData', 'price', 'description'],
  'fields.user': USER_FIELDS,
  'fields.image': ['variants.square-small', 'variants.square-small2x'],
};
const offerQueryParams = {
  processNames: OFFER_PROCESS_NAMES,
  lastTransitions: OFFER_LAST_TRANSITIONS,
  ...offerResponseQueryParams,
  perPage: OFFER_PAGE_SIZE,
  sort: 'lastTransitionedAt',
};

const entityRefs = entities =>
  entities.map(entity => ({
    id: entity.id,
    type: entity.type,
  }));

const withTransactions = (response, transactions) => ({
  ...response,
  data: {
    ...response?.data,
    data: transactions,
  },
});

const transactionUuid = tx => tx?.id?.uuid || null;

const includesTransaction = (transactions, transactionId) =>
  transactions.some(tx => transactionUuid(tx) === transactionId);

const mergePinnedTransaction = (transactions, pinnedTransaction) => {
  if (!pinnedTransaction || includesTransaction(transactions, transactionUuid(pinnedTransaction))) {
    return transactions;
  }

  return [pinnedTransaction, ...transactions];
};

const safeUuid = id => {
  try {
    return typeof id === 'string' && id.trim() ? new UUID(id.trim()) : null;
  } catch (_e) {
    return null;
  }
};

const fetchPinnedOfferMaybe = (sdk, createdOfferId) => {
  const id = safeUuid(createdOfferId);
  if (!id) {
    return Promise.resolve(null);
  }

  return sdk.transactions
    .show(
      {
        id,
        ...offerResponseQueryParams,
      },
      { expand: true }
    )
    .then(response => {
      const transaction = response?.data?.data || null;
      const offerTransactions = filterOfferConversations(transaction ? [transaction] : []);
      return offerTransactions[0] || null;
    })
    .catch(e => {
      log.warn(e, 'offers-pinned-offer-load-failed', {
        transactionId: createdOfferId,
        errorStatus: e?.status || e?.statusCode || null,
        errorCode: e?.code || null,
        errorMessage: e?.message || e?.statusText || null,
      });
      return null;
    });
};

const initialState = {
  fetchInProgress: false,
  fetchOffersError: null,
  pagination: null,
  transactionRefs: [],
  currentPage: 1,
  updateOfferInProgress: false,
  updateOfferSuccess: false,
  updateOfferError: null,
};

export default function offersPageReducer(state = initialState, action = {}) {
  const { type, payload, currentPage } = action;
  switch (type) {
    case FETCH_OFFERS_REQUEST:
      return {
        ...state,
        fetchInProgress: true,
        fetchOffersError: null,
        latestRequestId: action.requestId,
      };
    case FETCH_OFFERS_SUCCESS: {
      const transactions = sortedTransactions(payload.data.data || []);
      return {
        ...state,
        fetchInProgress: false,
        transactionRefs: entityRefs(transactions),
        pagination: payload.data.meta,
        currentPage: currentPage || 1,
      };
    }
    case FETCH_OFFERS_ERROR:
      return { ...state, fetchInProgress: false, fetchOffersError: payload };
    case UPDATE_OFFER_PROGRESS:
      return {
        ...state,
        updateOfferInProgress: true,
        updateOfferSuccess: false,
        updateOfferError: null,
      };
    case UPDATE_OFFER_SUCCESS:
      return {
        ...state,
        updateOfferInProgress: false,
        updateOfferSuccess: true,
        updateOfferError: null,
      };
    case UPDATE_OFFER_ERROR:
      return {
        ...state,
        updateOfferInProgress: false,
        updateOfferSuccess: false,
        updateOfferError: payload,
      };
    default:
      return state;
  }
}

const fetchOffersRequest = requestId => ({ type: FETCH_OFFERS_REQUEST, requestId });
const fetchOffersError = e => ({
  type: FETCH_OFFERS_ERROR,
  error: true,
  payload: e,
});
const updateOfferProgress = () => ({ type: UPDATE_OFFER_PROGRESS });
const updateOfferSuccess = () => ({ type: UPDATE_OFFER_SUCCESS });
const updateOfferError = e => ({ type: UPDATE_OFFER_ERROR, payload: e });

export const updateOffer = ({ id, transition, message = '', offerParams = null }) => async (
  dispatch,
  getState,
  sdk
) => {
  dispatch(updateOfferProgress());

  const txId = id?.uuid || id;
  const messageContent = typeof message === 'string' ? message.trim() : '';
  const transitionParams = offerParams
    ? {
        protectedData: {
          offerParams,
        },
      }
    : {};

  try {
    const response = await guardedTransaction({
      action: 'transition',
      bodyParams: { id, transition, params: transitionParams },
      queryParams: {
        expand: true,
        ...offerResponseQueryParams,
      },
    });
    dispatch(addMarketplaceEntities(response));

    triggerTransactionSmsNotifications({
      transactionId: id,
      source: 'OffersPage',
      transition,
    });

    if (messageContent) {
      await sendMarketplaceMessage({
        transactionId: id,
        content: messageContent,
        isOrderMessage: false,
        isOfferMessage: true,
        context: {
          source: 'OffersPage',
          transition,
          transactionId: txId,
          hasOffer: true,
          isOfferMessage: true,
          offerType: offerParams?.type || null,
        },
      });
    }

    dispatch(updateOfferSuccess());
    return response?.data?.data || null;
  } catch (e) {
    const error = storableError(e);
    log.error(e, 'offer-update-failed', {
      txId,
      transition,
      hasMessage: Boolean(messageContent),
      offerType: offerParams?.type || null,
      offerPrice: offerParams?.offerPrice || null,
      errorStatus: e?.status || e?.statusCode || null,
      errorCode: e?.code || null,
      errorMessage: e?.message || e?.statusText || null,
    });
    dispatch(updateOfferError(error));
    throw error;
  }
};

// Switching between Received and Sent quickly starts overlapping loads; only the latest counts
let offersRequestCount = 0;
const isStaleRequest = (getState, requestId) => {
  const latestRequestId = getState()?.OffersPage?.latestRequestId;
  return latestRequestId != null && latestRequestId !== requestId;
};

export const loadData = (params, search) => (dispatch, getState, sdk) => {
  offersRequestCount += 1;
  const requestId = offersRequestCount;
  dispatch(fetchOffersRequest(requestId));

  const { page = 1, createdOfferId } = parse(search);
  const currentPage = Number(page) || 1;
  // Offers the user sent as a buyer (/offers/sent), or received on their listings (/offers)
  const isSent = params?.view === 'sent';
  const queryParams = {
    ...offerQueryParams,
    only: isSent ? 'order' : 'sale',
    page: currentPage,
  };

  return sdk.transactions
    .query(queryParams)
    .then(async response => {
      const offerTransactions = filterOfferConversations(response?.data?.data || []);
      const shouldFetchPinnedOffer =
        isSent && createdOfferId && !includesTransaction(offerTransactions, createdOfferId);
      const pinnedOffer = shouldFetchPinnedOffer
        ? await fetchPinnedOfferMaybe(sdk, createdOfferId)
        : null;
      const transactions = mergePinnedTransaction(offerTransactions, pinnedOffer);
      const filteredResponse = withTransactions(response, transactions);
      if (isStaleRequest(getState, requestId)) {
        return filteredResponse;
      }
      dispatch(addMarketplaceEntities(filteredResponse));
      dispatch({
        type: FETCH_OFFERS_SUCCESS,
        payload: filteredResponse,
        currentPage,
      });
      return filteredResponse;
    })
    .catch(e => {
      if (isStaleRequest(getState, requestId)) {
        return null;
      }
      const error = storableError(e);
      log.error(e, 'offers-load-failed', {
        page: currentPage,
        transitionCount: OFFER_LAST_TRANSITIONS.length,
        errorStatus: e?.status || e?.statusCode || null,
        errorCode: e?.code || null,
        errorMessage: e?.message || e?.statusText || null,
      });
      dispatch(fetchOffersError(error));
      return null;
    });
};
